import { BuildServices, GenericRouteEvent, RouteEventAction, RouteEventActionColorMap, RouteEventActionFillMap, RouteEventActionTypeMap, Job } from ".";
import * as helpers from '../../helpers/ui-helpers';
import { DateTime } from "luxon";
import { GroupOnTheWayDialog } from "src/app/dialogs/grouped-stops/group-on-the-way/group-on-the-way.dialog";
import { GroupArriveDialog } from "src/app/dialogs/grouped-stops/group-arrive/group-arrive.dialog";
import { GroupPerformDialog } from "src/app/dialogs/grouped-stops/group-perform/group-perform.dialog";
import { GroupDetailsDialog } from "src/app/dialogs/grouped-stops/group-details/group-details.dialog";
import { GroupNotifyDialog } from "src/app/dialogs/grouped-stops/group-notify/group-notify.dialog";
import { environment } from "src/environments/environment";
import { Capacitor } from "@capacitor/core";

export class Group extends GenericRouteEvent {

  public routeEvents: GenericRouteEvent[] = [];

  constructor(routeEvents: GenericRouteEvent[], services: BuildServices) {
    super(services);
    const { location } = routeEvents?.find(re => re.location && helpers.hasValidGeo(re.location)) || {};
    this.routeEvents = routeEvents;
    this.location = location;
    this.stopSequence = routeEvents[0].stopSequence;
    this.type = 'group';
    this.completed = routeEvents.every(re => re.completed);
    this.setup();

    this.tapForDetails = async () => {
      const detailModal = await this.modalController.create({
        cssClass: 'parascope-full-modal',
        component: GroupDetailsDialog,
        componentProps: { routeEvents: this.routeEvents },
      });
      await detailModal.present();
      const dialog = detailModal.shadowRoot.querySelector('[role="dialog"]');
      dialog.setAttribute('aria-label', this.translateSvc.translate('TITLE.groupStop'));
    };
  }

  private setup() {
    this.scheduledStart = this.getScheduledStart();
    this.scheduledEnd = this.getScheduledEnd();
    this.typeIcons = this.getCardIcons();
    this.location = this.routeEvents?.find(re => helpers.hasValidGeo(re.location))?.location || null;
    this.headerInfo = {
      time: helpers.getTimeForCardHeader({ start: this.scheduledStart, end: this.scheduledEnd, translateSvc: this.translateSvc }),
      type: 'TITLE.groupStop',
      subtitle: this.getSubtitle(),
      timeTo: this.scheduledStart?.toRelative() || '',
    };
    this.detailBadges = this.getDetailBadges();
    this.isLate = this.completed ? false : helpers.isInThePast(this.scheduledStart);
    this.actions = this.getActions();
  }

  //  add an event to the group
  public addRouteEvent(routeEvent: GenericRouteEvent) {
    if (routeEvent.completed) { return; }
    this.routeEvents = [ ...(this.routeEvents || []), routeEvent ];
    this.setup();
  }

  public updateTimeBasedProperties = () => {
    const timeToDiff = this.scheduledStart;
    this.isLate = this.completed ? false : helpers.isInThePast(timeToDiff);
    this.routeEvents.forEach(re => {
      const _actions = re.getActions();
      if (JSON.stringify(_actions) !== JSON.stringify(re.actions)) { re.actions = _actions; }
    });
    const actions = this.getActions();
    if (JSON.stringify(actions) !== JSON.stringify(this.actions)) {
      this.actions = this.getActions();
    }
    this.headerInfo.timeTo = this.scheduledStart?.toRelative();
  };

  public getActions(): RouteEventAction[] {
    return [
      {
        type: RouteEventActionTypeMap.onTheWay,
        label: 'ACTION.onTheWay',
        show: this.routeEvents.some(event => event.actions.some(action => action.type === RouteEventActionTypeMap.onTheWay)),
        icon: 'send',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.onTheWay(action),
      },
      {
        type: RouteEventActionTypeMap.arrive,
        label: 'ACTION.arrive',
        show: this.routeEvents.some(event => event.actions.some(action => action.type === RouteEventActionTypeMap.arrive)),
        icon: 'locate',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.arrive({ action }),
      },
      {
        type: RouteEventActionTypeMap.perform,
        label: 'LABEL.perform',
        show: this.routeEvents.some(event => event.actions.some(action => action.type === RouteEventActionTypeMap.perform))
         && !this.routeEvents.some(event => event.actions.some(action => action.type === RouteEventActionTypeMap.arrive)),
        icon: 'grid-outline',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.perform(action),
      },
      {
        type: RouteEventActionTypeMap.depart,
        label: 'ACTION.depart',
        show: environment.features.groupStopPerformForceDepartAll
          ? this.routeEvents.every(event => event.actions.some(action => action.type === RouteEventActionTypeMap.depart))
          : this.routeEvents.some(event => event.actions.some(action => action.type === RouteEventActionTypeMap.depart)),
        icon: 'log-out', color: RouteEventActionColorMap.primary, fill: RouteEventActionFillMap.solid,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: (action: RouteEventAction) => this.depart({ action }),
      },
      {
        type: RouteEventActionTypeMap.navigate,
        label: `ACTION.navigate`,
        show: this.location && helpers.hasValidGeo(this.location),
        icon: 'navigate',
        color: RouteEventActionColorMap.primary,
        fill: RouteEventActionFillMap.outline,
        preview: false,
        drivingMode: true,
        disabled: false,
        fn: async (action: RouteEventAction) => this.navigate(action),
      },
      {
        type: RouteEventActionTypeMap.seeOn,
        label: `${ this.translateSvc.translate('LABEL.seeOn') } ${ this.translateSvc.translate('LABEL.map') }`,
        show: (this.appData.currentRouteIsPreview || Capacitor.getPlatform() !== 'ios')
          && this.location
          && helpers.hasValidGeo(this.location)
          && this.routeEvents.some(event => event.actions.some(action => action.type === RouteEventActionTypeMap.seeOn)),
        icon: 'map', color: RouteEventActionColorMap.medium,
        fill: RouteEventActionFillMap.outline,
        preview: true,
        drivingMode: false,
        disabled: false,
        fn: async (action: RouteEventAction) => this.seeOn(action),
      },
      {
        type: RouteEventActionTypeMap.notify,
        label: `ACTION.notify`,
        show: this.routeEvents.some(event => event.actions.some(action => action.type === RouteEventActionTypeMap.notify)),
        icon: 'call', color: RouteEventActionColorMap.medium, fill: RouteEventActionFillMap.outline,
        preview: false,
        drivingMode: false,
        disabled: false,
        fn: async (action: RouteEventAction) => this.notify(action),
      },
    ].filter(action => action.show);
    return [];
  }

  //  #region setup
  private getScheduledStart(): DateTime {
    return this.routeEvents.reduce((acc, routeEvent) => {
      if ((routeEvent.scheduledStart?.valueOf() || Infinity) < (acc?.valueOf() || Infinity)) {
        acc = routeEvent.scheduledStart;
      }
      return acc;
    }, this.routeEvents[0].scheduledStart);
  }

  private getScheduledEnd(): DateTime | null {
    const latestEnd = this.routeEvents.reduce((acc, routeEvent) => {
      if ((routeEvent.scheduledEnd?.valueOf() || 0) > (acc?.valueOf() || 0)) {
        acc = routeEvent.scheduledEnd;
      }
      return acc;
    }, this.routeEvents[0].scheduledEnd || 0);
    return latestEnd === 0 ? null : latestEnd as DateTime;
  }


  private getCardIcons() {
    return this.routeEvents.reduce((acc, routeEvent) => {
        routeEvent.typeIcons.forEach(icon => {
          if (!acc.find(_icon => _icon.name === icon.name)) { acc.push({
            ...icon,
            type: icon.type + ` ${ routeEvent.type }`,
          }); }
        });
        return acc;
      }, [])
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }

  private getSubtitle() {
    const counts = this.routeEvents.reduce((acc, re) => {
      if (acc[re.headerInfo.type]) {
        acc[re.headerInfo.type]++;
      } else {
        acc[re.headerInfo.type] = 1;
      }
      return acc;
    }, {});

    return Object.keys(counts).map(type => `${ counts[type] } ${this.translateSvc.translate(type + (counts[type] > 1 ? 's' : ''))}`)
      .toString()
      .replace(',', ', ');
  }

  private getDetailBadges(): helpers.Badge[] {
    return Array.from(
      new Set(this.routeEvents.filter(re => re instanceof Job)
      .map(({ value: { specialAssistances} }: Job) => specialAssistances)
      .flat()
      .sort(),
    )).map(value => ({
      class: 'special-assistance', color: 'medium', label: value,
    }));
  }
  //  #endregion

  // #region actions
  onTheWay = async (action?: RouteEventAction) => {
    try {
      if (action) { action.disabled = true; }
      const toOnTheWay = this.routeEvents
        .filter(event => event.actions.some(action => action.type === RouteEventActionTypeMap.onTheWay))
        .sort((a, b) => b.groupStopOrder - a.groupStopOrder);
      const dialog = await this.modalController.create({
        cssClass: 'parascope-full-modal',
        component: GroupOnTheWayDialog,
        componentProps: { routeEvents: toOnTheWay },
      });
      await dialog.present();
      const dialogRef = dialog.shadowRoot.querySelector('[role="dialog"]');
      dialogRef.setAttribute('aria-label', this.translateSvc.translate('TITLE.groupStop'));
      await dialog.onDidDismiss();
      this.actions = this.getActions();
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) { action.disabled = false; }
    }
  };

  arrive = async ({ action, checkHasArrived = true, checkOdometer = true }) => {
    try {
      if (action) { action.disabled = true; }
      const toArrive = this.routeEvents
        .filter(event => event.actions.some(action => action.type === RouteEventActionTypeMap.arrive))
        .sort((a, b) => b.groupStopOrder - a.groupStopOrder);
      const hasArrived = checkHasArrived ? await this.locationSvc.checkHasArrived(toArrive) : true;
      if (!hasArrived)  return;
      const odoChecked = checkOdometer ? await helpers.presentOdometer(this.modalController) : true;
      if (!odoChecked) return;
      if (environment.features.groupStopPerformForceArriveAll) {
        await this.dispatchSvc.arriveJobs(toArrive);
      } else {
        const dialog = await this.modalController.create({
          cssClass: 'parascope-full-modal',
          component: GroupArriveDialog,
          componentProps: { routeEvents: toArrive },
        });
        await dialog.present();
        const dialogRef = dialog.shadowRoot.querySelector('[role="dialog"]');
        dialogRef.setAttribute('aria-label', this.translateSvc.translate('TITLE.groupStop'));
        await dialog.onDidDismiss();
      }
      this.actions = this.getActions();
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) { action.disabled = false; }
    }
  };

  public perform = async (action?: RouteEventAction) => {
    try {
      if (action) { action.disabled = true; }
      const toPerform = this.routeEvents
        .filter(event => !event.completed)
        .filter(event => event.actions.some(action => action.type === RouteEventActionTypeMap.perform))
        .sort((a, b) => b.groupStopOrder - a.groupStopOrder);

      if (!toPerform.length) {
        this.actions = this.getActions();
        return;
      }
      const dialog = await this.modalController.create({
        cssClass: 'parascope-full-modal',
        component: GroupPerformDialog,
        id: 'groupPerformDialog',
        componentProps: { routeEvents: toPerform },
        backdropDismiss: false,
      });
      await dialog.present();
      const dialogRef = dialog.shadowRoot.querySelector('[role="dialog"]');
      dialogRef.setAttribute('aria-label', this.translateSvc.translate('TITLE.groupStop'));
      await dialog.onDidDismiss();
      if (this.routeEvents.every(re => re.completed)) {
        this.completed = true;
      }
      this.actions = this.getActions();
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) { action.disabled = false; }
    }
  };

  depart = async ({ action }) => {
    try {
      if (action) { action.disabled = true; }
      const toDepart = this.routeEvents.filter(event => event.actions.some(action => action.type === RouteEventActionTypeMap.depart));
      await this.dispatchSvc.departJobs(toDepart);
      this.actions = this.getActions();
      this.completed = true;
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) { action.disabled = false; }
    }
  };

  private navigate(action: RouteEventAction) {
    try {
      if (action) { action.disabled = true; }
      this.locationSvc.navigateToDestination(this.location.coordinate);
    } catch(error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) { this.disabled = false; }
    }
  }

  private seeOn(action?: RouteEventAction) {
    try {
      if (action) { action.disabled = true; }
      this.locationSvc.previewLocation(this.location.coordinate, this.location.name);
    } catch(error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) { this.disabled = false; }
    }
  }

  public notify = async (action?: RouteEventAction) => {
    try {
      if (action) { action.disabled = true; }
      const toNotify = this.routeEvents
        .filter(event => event.actions.some(action => action.type === RouteEventActionTypeMap.notify))
        .sort((a, b) => b.groupStopOrder - a.groupStopOrder);
      const dialog = await this.modalController.create({
        cssClass: 'parascope-full-modal',
        component: GroupNotifyDialog,
        componentProps: { routeEvents: toNotify },
      });
      await dialog.present();
      const dialogRef = dialog.shadowRoot.querySelector('[role="dialog"]');
      dialogRef.setAttribute('aria-label', this.translateSvc.translate('LABEL.notify'));
      await dialog.onDidDismiss();
    } catch (error) {
      this.notificationSvc.error(error.message || error);
    } finally {
      if (action) { action.disabled = false; }
    }
  };

}
